// Note: amazon-chime-sdk-component-library-react and amazon-chime-sdk-js need to be installed
// npm install amazon-chime-sdk-component-library-react amazon-chime-sdk-js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSubscription, useMutation } from '@apollo/client/react';
import {
  MeetingProvider,
  useMeetingManager,
  useLocalVideo,
  useToggleLocalMute,
  useAudioVideo,
  useRosterState,
  useContentShareState,
  useContentShareControls,
  VideoTileGrid,
  MeetingStatus,
  useMeetingStatus,
  RosterAttendeeType,
  lightTheme,
  GlobalStyles,
} from 'amazon-chime-sdk-component-library-react';
import { ThemeProvider } from 'styled-components';
import { 
  MeetingSessionConfiguration,
  MeetingSessionStatus,
  MeetingSessionStatusCode,
} from 'amazon-chime-sdk-js';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { VideoControls } from './VideoControls';
import { WaitingRoomPanel } from './WaitingRoomPanel';
import { InCallChat } from './InCallChat';
import { ReactionAnimation } from './ReactionAnimation';
import { BackgroundSettings } from './BackgroundSettings';
import { AddParticipantModal } from './AddParticipantModal';
import { useInCallChat } from '../../hooks/useInCallChat';
import { useReactions, ReactionEmoji } from '../../hooks/useReactions';
import { useRaiseHand } from '../../hooks/useRaiseHand';
import { useVirtualBackground } from '../../hooks/useVirtualBackground';
import { ON_VIDEO_CONFERENCE_UPDATED } from '../../graphql/queries';
import { MUTE_ALL_PARTICIPANTS } from '../../graphql/videoConferences';
import toast from 'react-hot-toast';

// Use styled-components ThemeProvider with type assertion for compatibility
const StyledThemeProvider = ThemeProvider as React.ComponentType<{ theme: any; children: React.ReactNode }>;

// Type definitions for Apollo subscriptions
interface VideoConference {
  id: string;
  status: string;
  waitingRoomEnabled?: boolean;
  waitingRoomParticipants?: WaitingParticipant[];
  muteAllEnabled?: boolean;
  preventUnmuteEnabled?: boolean;
  [key: string]: any;
}

interface WaitingParticipant {
  id: string;
  name: string;
  participantType: 'CLIENT' | 'INQUIRY' | 'GUEST' | 'STAFF';
  requestedAt: string;
}

interface OnVideoConferenceUpdatedData {
  onVideoConferenceUpdated: VideoConference;
}

// Custom dark theme for video calls
const darkTheme = {
  ...lightTheme,
  colors: {
    ...lightTheme.colors,
    primary: {
      lightest: '#88b2ff',
      lighter: '#5c93ff',
      light: '#3174ff',
      main: '#075fff',
      dark: '#004ce6',
      darker: '#003cb3',
      darkest: '#002c80',
    },
    greys: {
      black: '#000000',
      grey100: '#1b1c20',
      grey90: '#2e2f34',
      grey80: '#3f4149',
      grey70: '#50525e',
      grey60: '#616473',
      grey50: '#7d818b',
      grey40: '#989ca5',
      grey30: '#b4b7bf',
      grey20: '#d0d2d8',
      grey10: '#ecedf1',
      white: '#ffffff',
    },
  },
  videoGrid: {
    bgd: '#1b1c20',
  },
};

interface ChimeMeetingCredentials {
  meetingId: string;
  mediaRegion: string;
  attendeeId: string;
  joinToken: string;
  externalUserId: string;
  meeting: any;
  attendee: any;
}

interface VideoCallInterfaceProps {
  credentials: ChimeMeetingCredentials;
  conferenceId: string;
  conferenceTitle: string;
  isHost: boolean;
  isRecordingEnabled: boolean;
  currentUserId: string;
  currentUserName: string;
  currentUserRole: string;
  waitingRoomEnabled?: boolean;
  initialWaitingParticipants?: WaitingParticipant[];
  canEndMeeting?: boolean; // Host or staff who started the meeting
  onEndCall: () => void;
  onLeaveCall: () => void;
}

// Inner component that uses Chime hooks
function VideoCallContent({
  conferenceId,
  conferenceTitle,
  isHost,
  isRecordingEnabled,
  currentUserId,
  currentUserName,
  currentUserRole,
  waitingRoomEnabled: initialWaitingRoomEnabled = false,
  initialWaitingParticipants = [],
  canEndMeeting = false,
  onEndCall,
  onLeaveCall,
}: Omit<VideoCallInterfaceProps, 'credentials'>) {
  const meetingManager = useMeetingManager();
  const meetingStatus = useMeetingStatus();
  const { toggleVideo, isVideoEnabled } = useLocalVideo();
  const { muted, toggleMute } = useToggleLocalMute();
  const audioVideo = useAudioVideo();
  const { roster } = useRosterState();
  const { isLocalUserSharing } = useContentShareState();
  const { toggleContentShare } = useContentShareControls();

  const [showParticipants, setShowParticipants] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [showBackgroundSettings, setShowBackgroundSettings] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [networkQuality, setNetworkQuality] = useState<'good' | 'fair' | 'poor'>('good');
  const [callEnded, setCallEnded] = useState(false);
  const [waitingParticipants, setWaitingParticipants] = useState<WaitingParticipant[]>(initialWaitingParticipants);
  const [isMutedByHost, setIsMutedByHost] = useState(false);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  
  // Note: Attendee names are populated by the getAttendee callback set in VideoCallInterface before joining

  // Mutations
  const [muteAllParticipants] = useMutation(MUTE_ALL_PARTICIPANTS);

  // Check if user can manage (host or admin)
  const canManage = isHost || currentUserRole === 'ADMIN';

  // In-call chat hook
  const {
    messages: chatMessages,
    sendMessage: sendChatMessage,
    unreadCount: unreadChatCount,
    clearUnread: clearChatUnread,
    isConnected: isChatConnected,
  } = useInCallChat({
    conferenceId,
    currentUserId,
    currentUserName,
    audioVideo,
  });

  // Reactions hook
  const { reactions, sendReaction } = useReactions({
    currentUserId,
    currentUserName,
    audioVideo,
  });

  // Raise hand hook
  const { isHandRaised, raisedHands, toggleHand, lowerHand } = useRaiseHand({
    currentUserId,
    currentUserName,
    audioVideo,
  });

  // Virtual background hook
  const {
    currentBackground,
    isApplying: isApplyingBackground,
    isSupported: isBackgroundSupported,
    error: backgroundError,
    applyBackground,
  } = useVirtualBackground({
    meetingSession: meetingManager.meetingSession,
  });

  // Get participant count (filter out MediaPipeline attendees used for recording)
  const filteredRoster = Object.entries(roster).filter(([_, attendee]: [string, RosterAttendeeType]) => {
    const name = attendee.name || '';
    // Filter out AWS MediaPipeline (recording) and other system attendees
    return !name.startsWith('aws:') && !name.includes('MediaPipeline');
  });
  const participantCount = filteredRoster.length;

  // Subscribe to conference ended event (for non-host participants)
  const handleConferenceEnded = useCallback(async () => {
    setCallEnded(true);
    toast('The host has ended the call', {
      duration: 5000,
      icon: '📞',
    });
    try {
      await meetingManager.leave();
    } catch (error) {
      console.error('Error leaving meeting after host ended:', error);
    }
    // Also notify parent component so it can update its state
    onLeaveCall();
  }, [meetingManager, onLeaveCall]);

  const { data: updatedData } = useSubscription<OnVideoConferenceUpdatedData>(ON_VIDEO_CONFERENCE_UPDATED, {
    variables: { conferenceId },
    skip: !conferenceId,
    onData: ({ data }) => {
      const updated = data?.data?.onVideoConferenceUpdated;
      if (updated) {
        // Handle conference ended
        if (!isHost && (updated.status === 'COMPLETED' || updated.status === 'CANCELLED')) {
          handleConferenceEnded();
        }
        // Update waiting room participants
        if (updated.waitingRoomParticipants) {
          setWaitingParticipants(updated.waitingRoomParticipants);
        }
        // Handle mute all
        if (updated.muteAllEnabled && !canManage) {
          setIsMutedByHost(true);
          toast('You have been muted by the host', { icon: '🔇' });
        }
        if (!updated.muteAllEnabled && isMutedByHost) {
          setIsMutedByHost(false);
          toast('You can now unmute yourself', { icon: '🔊' });
        }
      }
    },
  });

  // Suppress unused variable warning
  void updatedData;

  // AudioVideoObserver for meeting lifecycle events and network quality
  // This is the recommended way to detect meeting end per Chime SDK best practices
  useEffect(() => {
    if (!audioVideo) return;

    const observer = {
      // Network quality monitoring
      connectionDidBecomePoor: () => setNetworkQuality('poor'),
      connectionDidSuggestStopVideo: () => setNetworkQuality('fair'),
      connectionDidBecomeGood: () => setNetworkQuality('good'),
      
      // Meeting lifecycle - this is the most reliable way to detect meeting end
      audioVideoDidStop: (sessionStatus: MeetingSessionStatus) => {
        const statusCode = sessionStatus.statusCode();
        console.log('[VideoCallInterface] audioVideoDidStop with status:', statusCode);
        
        // Handle different meeting end scenarios
        if (statusCode === MeetingSessionStatusCode.MeetingEnded) {
          // Meeting was ended by host or server
          console.log('[VideoCallInterface] Meeting ended by host/server');
          setCallEnded(true);
          if (!isHost) {
            toast('The host has ended the meeting', { duration: 5000, icon: '📞' });
          }
        } else if (statusCode === MeetingSessionStatusCode.Left) {
          // User left voluntarily - already handled by leave button
          console.log('[VideoCallInterface] User left meeting');
        } else if (statusCode === MeetingSessionStatusCode.AudioJoinedFromAnotherDevice) {
          // User joined from another device
          setCallEnded(true);
          toast('You joined from another device', { duration: 5000, icon: '📱' });
        } else if (statusCode === MeetingSessionStatusCode.AudioDisconnected) {
          // Audio disconnected - could be network issue
          console.log('[VideoCallInterface] Audio disconnected');
          toast.error('Connection lost. Please rejoin the meeting.');
          setCallEnded(true);
        }
      },
      
      // Called when session starts connecting
      audioVideoDidStartConnecting: (reconnecting: boolean) => {
        if (reconnecting) {
          console.log('[VideoCallInterface] Reconnecting to meeting...');
          toast('Reconnecting...', { icon: '🔄' });
        }
      },
      
      // Called when session successfully connects
      audioVideoDidStart: () => {
        console.log('[VideoCallInterface] Successfully connected to meeting');
      },
    };

    audioVideo.addObserver(observer);
    return () => audioVideo.removeObserver(observer);
  }, [audioVideo, isHost]);

  // Handle leaving the meeting
  const handleLeave = async () => {
    // If user can end meeting (host or starter), show confirmation
    if (canEndMeeting) {
      setShowLeaveConfirmation(true);
      return;
    }
    
    try {
      await meetingManager.leave();
      onLeaveCall();
    } catch (error) {
      console.error('Error leaving meeting:', error);
      onLeaveCall();
    }
  };

  // Handle confirmed leave (ends meeting for host/starter)
  const handleConfirmedLeave = async () => {
    setShowLeaveConfirmation(false);
    try {
      await meetingManager.leave();
      onEndCall(); // End the meeting when host/starter leaves
    } catch (error) {
      console.error('Error ending meeting:', error);
      onEndCall();
    }
  };

  // Handle ending the meeting (host only)
  const handleEnd = async () => {
    try {
      await meetingManager.leave();
      onEndCall();
    } catch (error) {
      console.error('Error ending meeting:', error);
      onEndCall();
    }
  };

  // Handle screen share toggle
  const handleScreenShare = async () => {
    try {
      await toggleContentShare();
    } catch (error) {
      console.error('Error toggling screen share:', error);
    }
  };

  // Handle mute all
  const handleMuteAll = async () => {
    try {
      await muteAllParticipants({
        variables: { conferenceId, preventUnmute: false },
      });
      toast.success('All participants muted');
    } catch (error: any) {
      toast.error(error.message || 'Failed to mute all');
    }
  };

  // Handle add participant
  const handleAddParticipant = () => {
    setShowAddParticipant(true);
  };

  // Handle chat toggle
  const handleToggleChat = () => {
    setShowChat(!showChat);
    if (!showChat) {
      clearChatUnread();
    }
  };

  // Handle reaction
  const handleSendReaction = (emoji: ReactionEmoji) => {
    sendReaction(emoji);
  };

  if (meetingStatus === MeetingStatus.Loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Joining meeting...</p>
        </div>
      </div>
    );
  }

  if (meetingStatus === MeetingStatus.Failed) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">Failed to join meeting</p>
          <Button onClick={onLeaveCall}>Go Back</Button>
        </div>
      </div>
    );
  }

  // Handle meeting ended by Chime SDK (e.g., host ended the meeting) or callEnded state
  if (meetingStatus === MeetingStatus.Ended || callEnded) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg p-1.5 mx-auto mb-6 w-fit">
            <img src="/icon.png" alt="AALaw" className="h-10 w-10" />
          </div>
          <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-3">This conference has ended</h1>
          <div className="bg-gray-800 rounded-lg p-4 mb-8">
            <p className="text-white font-medium">{conferenceTitle}</p>
          </div>
          <Button
            onClick={onLeaveCall}
            className="w-full bg-white text-gray-900 hover:bg-gray-100 py-3 text-lg font-medium"
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 flex flex-col">
      {/* Reaction Animations Overlay */}
      <ReactionAnimation reactions={reactions} />

      {/* Header - responsive for mobile */}
      <div className="bg-gray-800 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="bg-white rounded-md p-0.5 flex-shrink-0">
            <img src="/icon.png" alt="AALaw" className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <h1 className="text-white font-semibold text-sm sm:text-base truncate">{conferenceTitle}</h1>
          {isRecordingEnabled && (
            <Badge variant="destructive" className="animate-pulse text-xs flex-shrink-0">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-1 sm:mr-2"></span>
              <span className="hidden sm:inline">Recording</span>
              <span className="sm:hidden">REC</span>
            </Badge>
          )}
          {isMutedByHost && (
            <Badge variant="secondary" className="bg-yellow-600 text-xs flex-shrink-0">
              <span className="hidden sm:inline">Muted by host</span>
              <span className="sm:hidden">Muted</span>
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* Network Quality Indicator - simplified on mobile */}
          <div className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${
              networkQuality === 'good' ? 'bg-green-500' :
              networkQuality === 'fair' ? 'bg-yellow-500' : 'bg-red-500'
            }`}></div>
            <span className="hidden sm:inline text-gray-400 text-sm capitalize">{networkQuality}</span>
          </div>

          {/* Waiting Room Button (Host/Admin only) - always show since waiting room is always enabled */}
          {canManage && (
            <button
              onClick={() => setShowWaitingRoom(!showWaitingRoom)}
              className={`relative flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg transition-colors ${
                showWaitingRoom ? 'bg-aalaw-red' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="hidden sm:inline text-white text-sm">Waiting</span>
              {waitingParticipants.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                  {waitingParticipants.length}
                </span>
              )}
            </button>
          )}
          
          {/* Participant Count */}
          <button
            onClick={() => setShowParticipants(!showParticipants)}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="text-white text-sm">{participantCount}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex relative overflow-hidden">
        {/* Video Grid - includes local and remote videos */}
        {/* Video grid - responsive padding */}
        <div className={`flex-1 p-2 sm:p-4 transition-all ${showParticipants || showChat ? 'sm:pr-80' : ''}`}>
          <VideoTileGrid
            className="h-full"
            noRemoteVideoView={
              participantCount > 1 ? (
                <div className="h-full flex items-center justify-center bg-gray-800 rounded-lg">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <p className="text-gray-400 text-sm sm:text-base">Camera is off</p>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-800 rounded-lg">
                  <p className="text-gray-400 text-sm sm:text-base px-4 text-center">Waiting for participant to join...</p>
                </div>
              )
            }
          />
        </div>

        {/* Participants Panel - full screen on mobile */}
        {showParticipants && (
          <div className="absolute inset-0 sm:right-0 sm:left-auto sm:w-80 bg-gray-800 sm:border-l border-gray-700 overflow-y-auto z-20">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-white font-semibold">Participants ({participantCount})</h2>
              <button 
                onClick={() => setShowParticipants(false)}
                className="sm:hidden p-2 text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-2">
              {filteredRoster.map(([attendeeId, attendee]: [string, RosterAttendeeType]) => {
                const hasRaisedHand = raisedHands.has(attendeeId);
                // Get name from roster (populated by getAttendee callback set before joining)
                const displayName = attendee.name || 'Unknown';
                return (
                  <div
                    key={attendeeId}
                    className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg"
                  >
                    <div className="relative w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {displayName.charAt(0).toUpperCase()}
                      </span>
                      {hasRaisedHand && (
                        <span className="absolute -top-1 -right-1 text-lg">✋</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{displayName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {(attendee as any).muted && (
                          <span className="text-red-400 text-xs">Muted</span>
                        )}
                        {hasRaisedHand && canManage && (
                          <button
                            onClick={() => lowerHand(attendeeId)}
                            className="text-xs text-yellow-400 hover:text-yellow-300"
                          >
                            Lower hand
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Chat Panel - full screen on mobile */}
        {showChat && (
          <div className="absolute inset-0 sm:right-0 sm:left-auto sm:w-80 z-20">
            <InCallChat
              messages={chatMessages}
              onSendMessage={sendChatMessage}
              unreadCount={unreadChatCount}
              onClearUnread={clearChatUnread}
              isOpen={true}
              onToggle={handleToggleChat}
              isConnected={isChatConnected}
            />
          </div>
        )}

        {/* Waiting Room Panel (Host/Admin only) */}
        {showWaitingRoom && canManage && (
          <div className="absolute left-4 top-4 w-80 z-30">
            <WaitingRoomPanel
              conferenceId={conferenceId}
              waitingParticipants={waitingParticipants}
              waitingRoomEnabled={initialWaitingRoomEnabled}
              onParticipantsChange={setWaitingParticipants}
            />
          </div>
        )}
      </div>

      {/* Controls Bar */}
      <VideoControls
        isMuted={muted}
        isVideoEnabled={isVideoEnabled}
        isScreenSharing={isLocalUserSharing}
        isHost={isHost}
        onToggleMute={toggleMute}
        onToggleVideo={toggleVideo}
        onToggleScreenShare={handleScreenShare}
        onLeave={handleLeave}
        onEnd={handleEnd}
        // New props
        isChatOpen={showChat}
        unreadChatCount={unreadChatCount}
        onToggleChat={handleToggleChat}
        isHandRaised={isHandRaised}
        onToggleHand={toggleHand}
        onSendReaction={handleSendReaction}
        onOpenBackgroundSettings={() => setShowBackgroundSettings(true)}
        onMuteAll={canManage ? handleMuteAll : undefined}
        onAddParticipant={canManage ? handleAddParticipant : undefined}
      />

      {/* Background Settings Modal */}
      <BackgroundSettings
        currentBackground={currentBackground}
        isApplying={isApplyingBackground}
        isSupported={isBackgroundSupported}
        error={backgroundError}
        onApplyBackground={applyBackground}
        isOpen={showBackgroundSettings}
        onClose={() => setShowBackgroundSettings(false)}
      />

      {/* Add Participant Modal */}
      <AddParticipantModal
        conferenceId={conferenceId}
        isOpen={showAddParticipant}
        onClose={() => setShowAddParticipant(false)}
      />

      {/* Leave Confirmation Modal (for host/starter) */}
      {showLeaveConfirmation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">End Meeting?</h2>
              <p className="text-gray-400">
                Leaving will end the meeting for all participants. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowLeaveConfirmation(false)}
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmedLeave}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                End Meeting
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main component with MeetingProvider
export function VideoCallInterface(props: VideoCallInterfaceProps) {
  const meetingManager = useMeetingManager();
  const [isJoining, setIsJoining] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasJoinedRef = useRef(false);

  useEffect(() => {
    // Guard against double-joining (React StrictMode or re-renders)
    if (hasJoinedRef.current) {
      return;
    }
    
    const joinMeeting = async () => {
      if (hasJoinedRef.current) {
        return;
      }
      hasJoinedRef.current = true;
      
      try {
        // IMPORTANT: Set up getAttendee callback BEFORE joining the meeting
        // This callback is used by the SDK to resolve attendee names from externalUserId
        meetingManager.getAttendee = async (
          _chimeAttendeeId: string,
          externalUserId?: string
        ): Promise<{ name: string }> => {
          // externalUserId format is "Name#ID" - extract the name part
          if (externalUserId) {
            const name = externalUserId.includes('#') 
              ? externalUserId.split('#')[0] 
              : externalUserId;
            return { name };
          }
          return { name: 'Unknown' };
        };
        
        const meetingSessionConfiguration = new MeetingSessionConfiguration(
          props.credentials.meeting,
          props.credentials.attendee
        );

        await meetingManager.join(meetingSessionConfiguration);
        await meetingManager.start();
        setIsJoining(false);
      } catch (err: any) {
        console.error('Error joining meeting:', err.message);
        setError(`Failed to join the meeting: ${err.message || 'Unknown error'}`);
        setIsJoining(false);
        hasJoinedRef.current = false; // Allow retry on error
      }
    };

    joinMeeting();

    // Handle window/tab close - ensure we leave the meeting
    // For host/starter, show browser confirmation before closing
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // If canEndMeeting prop is true, show browser confirmation
      if (props.canEndMeeting) {
        event.preventDefault();
        event.returnValue = 'Leaving will end the meeting for all participants. Are you sure?';
        return event.returnValue;
      }
      meetingManager.leave();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // If host/starter closes window, end the meeting
      if (props.canEndMeeting) {
        props.onEndCall();
      }
      meetingManager.leave();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isJoining) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Connecting to meeting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <Button onClick={props.onLeaveCall}>Go Back</Button>
        </div>
      </div>
    );
  }

  return <VideoCallContent {...props} />;
}

// Wrapper component that provides MeetingProvider and ThemeProvider
export function VideoCallWrapper(props: VideoCallInterfaceProps) {
  return (
    <StyledThemeProvider theme={darkTheme}>
      <GlobalStyles />
      <MeetingProvider>
        <VideoCallInterface {...props} />
      </MeetingProvider>
    </StyledThemeProvider>
  );
}
